import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import IntroSection from "../components/IntroSection";
import IntroSectionSkeleton from "../components/IntroSectionSkeleton";
import { useAuth } from "../context/AuthContext";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import { api } from "../lib/api";
import { useIntro } from "../hooks/useIntro";
import {
  Sparkles, Compass, GraduationCap, Database, Bot, FileCheck,
  CheckCircle2, ArrowRight, PlayCircle, PhoneCall,
} from "lucide-react";

// Default Welcome intro — admins override copy/video from Admin → Intros
// (backend key: "welcome"). Same drop-in as COURSE_INTRO / BLUEPRINT_INTRO.
// Paste any of these into videoUrl:
//   • YouTube share URL          "https://youtu.be/XXXXXXX"
//   • YouTube embed URL          "https://www.youtube.com/embed/XXXXXXX"
//   • Vimeo player URL           "https://player.vimeo.com/video/XXXXXXX"
//   • Direct MP4 / WEBM / MOV    "https://cdn.example.com/intro.mp4"
export const WELCOME_INTRO = {
  eyebrow: "Welcome to CPG Institute",
  title: "Let's launch your brand.",
  body: "Everything Warren has learned across 25+ years of building and scaling CPG brands is waiting for you inside. Watch the quick tour, then work through the getting-started steps below.",
  videoUrl: "https://customer-assets-wrfwihn1.emergentagent.net/job_cpg-institute-ux/artifacts/l12oeqyk_The%20CPG%20Institute%20Introduction_1080p.mp4",
  bullets: [
    "17 core modules · quizzes, worksheets, and specialist certifications",
    "Live databases: 400+ retailers, 300+ investors, vetted industry vendors",
    "AI Studio + Morgan — from packaging concepts to buyer emails",
  ],
};

const STEPS = [
  {
    icon: PlayCircle,
    title: "Watch the intro (5 min)",
    desc: "Warren walks you through what's in every tier and how to work through the curriculum.",
  },
  {
    icon: GraduationCap,
    title: "Start Module 1",
    desc: "Company & Legal Setup — the foundation every fundable CPG brand needs before shelf.",
    to: "/course",
    cta: "Open the course",
  },
  {
    icon: Bot,
    title: "Meet Morgan",
    desc: "Ask the assistant anything about a module, buyer, margin, or pitch. Auto-routes to Claude & ChatGPT.",
    to: "/assistant",
    cta: "Chat with Morgan",
  },
  {
    icon: FileCheck,
    title: "Draft your Launch Blueprint",
    desc: "You can save-as-you-go — the Warren team reviews each submission personally.",
    to: "/blueprint",
    cta: "Open the Blueprint",
  },
];

const QUICK_LINKS = [
  { icon: GraduationCap, label: "Start the Course", to: "/course", testid: "welcome-quick-course" },
  { icon: Database, label: "Explore the Databases", to: "/databases", testid: "welcome-quick-databases" },
  { icon: Bot, label: "Ask Morgan", to: "/assistant", testid: "welcome-quick-assistant" },
  { icon: FileCheck, label: "Launch Blueprint", to: "/blueprint", testid: "welcome-quick-blueprint" },
];

const FIRST_WEEK = [
  "Finish Module 1: Company & Legal Setup",
  "Bookmark 5 target retailers in the Retailers database",
  "Save 10 investors that match your stage in Investors",
  "Sketch your positioning in the Launch Blueprint",
  "Ask Warren AI to review your one-liner",
];

export default function Welcome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const first = (user?.name || "there").split(/\s+/)[0];
  const { intro, ready: introReady } = useIntro("welcome", WELCOME_INTRO);
  const [checks, setChecks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("welcomeChecks") || "[]");
    } catch { return []; }
  });

  const toggle = (i) => {
    const next = checks.includes(i) ? checks.filter((x) => x !== i) : [...checks, i];
    setChecks(next);
    try { localStorage.setItem("welcomeChecks", JSON.stringify(next)); } catch {}
  };

  return (
    <AppShell title="Welcome">
      <div className="w-full">
        {/* Intro hero — same layout as Course & Launch Blueprint pages.
          * Skeleton renders on cold visits (incognito / new device /
          * cache-cleared) while the DB round-trip completes so the
          * hardcoded fallback copy never flashes on the first paint. */}
        {introReady && intro ? (
          <IntroSection
            {...intro}
            title={intro.title === WELCOME_INTRO.title
              ? `Hey ${first} — ${intro.title.toLowerCase()}`
              : intro.title}
            progressKey="welcome_intro"
            ctaLabel="Jump to getting-started"
            ctaHref="#welcome-steps"
            allowSeek
            configHint={<>Admin → Intros to edit copy + video. Fallback in <code>src/pages/Welcome.jsx</code>.</>}
            testId="welcome-intro"
          />
        ) : (
          <IntroSectionSkeleton testId="welcome-intro" />
        )}

        {/* ─────────────────────── Steps ─────────────────────── */}
        <section id="welcome-steps" className="mt-14">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold">Getting Started</p>
          <h2 className="font-display text-3xl md:text-4xl mt-2">
            The best way to <span className="text-grad">use CPG Institute</span>
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {STEPS.map((s, i) => (
              <StepCard key={s.title} n={i + 1} {...s} onGo={s.to ? () => nav(s.to) : undefined} />
            ))}
          </div>
        </section>

        {/* ─────────────────────── Quick links ─────────────────────── */}
        <section className="mt-14">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold">Jump In</p>
          <h2 className="font-display text-3xl md:text-4xl mt-2">
            Quick links to your <span className="text-grad">command center</span>
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK_LINKS.map(({ icon: Icon, label, to, testid }) => (
              <Link key={to} to={to} data-testid={testid}
                className="group card-elevated p-5 flex items-center gap-4 hover:border-primary hover:shadow-md transition-all">
                <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" />
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{label}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </section>

        {/* ─────────────────────── First-week checklist ─────────────────────── */}
        <section className="mt-14 grid md:grid-cols-[1fr_1fr] gap-6">
          <div className="card-elevated p-6" data-testid="welcome-checklist">
            <div className="flex items-center gap-2">
              <Compass className="w-5 h-5 text-primary" />
              <p className="font-display text-2xl">Your first-week checklist</p>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Tap each item as you complete it.</p>
            <ul className="mt-4 grid gap-2">
              {FIRST_WEEK.map((label, i) => {
                const done = checks.includes(i);
                return (
                  <li key={i}>
                    <button onClick={() => toggle(i)} data-testid={`welcome-check-${i}`}
                      className={["w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors",
                        done ? "bg-primary/5 border-primary/30 line-through text-muted-foreground" : "border-shell-border hover:border-primary/40"].join(" ")}>
                      <span className={["w-5 h-5 rounded-full border grid place-items-center shrink-0",
                        done ? "bg-primary border-primary text-white" : "border-shell-border"].join(" ")}>
                        {done && <CheckCircle2 className="w-4 h-4" />}
                      </span>
                      <span>{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Warren note */}
          <div className="card-elevated p-6 relative overflow-hidden" data-testid="welcome-note">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
            <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">A Note from Warren</p>
            <p className="font-display text-2xl mt-2">You don't need to figure this out alone.</p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              I built CPG Institute because I was tired of watching brilliant founders burn cash
              on the same 5 mistakes I made 20 years ago. Every module, database, and template in
              here exists because someone I coached asked for it. Use them. Break them. Come back
              with better questions.
            </p>
            <p className="mt-4 text-sm text-muted-foreground italic">— Warren Ezra, Founder</p>
            <Link to="/assistant" data-testid="welcome-note-ask"
              className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary hover:underline">
              Ask a question <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* ─────────────────────── Book a call CTA ─────────────────────── */}
        <BookCallCTA />
      </div>
    </AppShell>
  );
}

/**
 * Purple "Book a 1-on-1 with Warren" section. Extracted into its own
 * component so it can call `useMenuVisibility()` inside the AppShell
 * provider AND return null cleanly when admin toggles it hidden via
 * Menu Directory → Pages → Welcome → Book a 1-on-1 with Warren. No
 * blank space or empty container remains — the parent `<div>`'s flex
 * flow just closes up.
 */
function BookCallCTA() {
  const { isPageElementHidden } = useMenuVisibility();
  if (isPageElementHidden("welcome-book-1on1")) return null;
  return (
    <section className="mt-14 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden on-dark"
      style={{ background: "linear-gradient(135deg, #6A0DAD 0%, #B2007A 55%, #E70091 100%)" }}
      data-testid="welcome-call-cta"
      data-surface="dark">
      <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-white/10 blur-3xl" />
      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/80">Want a shortcut?</p>
          <h2 className="font-display text-3xl md:text-4xl mt-2">Book a 1-on-1 with Warren.</h2>
          <p className="mt-2 text-white/85 max-w-xl">
            Bring your product, your pitch, or your problem. 30 minutes to unblock what's next.
          </p>
        </div>
        <a href="mailto:hello@cpg.institute?subject=1-on-1%20call%20request" data-testid="welcome-book-call"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-white/90 transition self-start md:self-auto">
          <PhoneCall className="w-4 h-4" /> Book a call
        </a>
      </div>
    </section>
  );
}

function StepCard({ n, icon: Icon, title, desc, cta, onGo }) {
  return (
    <div className="card-elevated p-6 flex gap-5" data-testid={`welcome-step-${n}`}>
      <div className="shrink-0 flex flex-col items-center">
        <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-sky-400 text-white grid place-items-center shadow-md">
          <Icon className="w-5 h-5" />
        </span>
        <span className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Step {n}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-xl">{title}</p>
        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{desc}</p>
        {cta && onGo && (
          <button onClick={onGo} className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-primary hover:underline">
            {cta} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
